/*
 * ft-calc-buy.js
 * ------------------------------------------------------------------
 * Shared "buy from calculator" engine (wood fence, mud grid, paddock slab,
 * gym mat). Mirrors the stall-mat calculator: pick a product (where there's a
 * choice), a live price summary updates from the calculator's computed
 * quantity, and one button adds the exact quantities to the cart.
 *
 * Declarative markup:
 *   [data-ftbuy-panel]                         one add-to-cart scope
 *     [data-ftbuy-tiles="KEY"]                 a product group
 *       role="radiogroup" (choice groups)      + aria-labelledby the group label
 *       data-ftbuy-qty-selector="<sel>"        element whose text is the count
 *       data-ftbuy-mode="choice" | "auto"      user picks, or auto by qty tier
 *       [data-ftbuy-tile] role="radio"         one product option
 *         data-price, data-variant-id|data-product-handle, data-name
 *         data-min / data-max (auto mode: qty range)
 *     [data-ftbuy-line="KEY"]                   summary row
 *       [data-ftbuy-line-qty] [data-ftbuy-line-unit] [data-ftbuy-line-total] [data-ftbuy-line-name]
 *     [data-ftbuy-total]                        grand total
 *     [data-ftbuy-error]                        inline error / hint region
 *     [data-ftbuy-add]                          add-to-cart button
 */
(function () {
  'use strict';

  function money(n) {
    if (!isFinite(n)) n = 0;
    return '$' + (Math.round(n * 100) / 100).toFixed(2);
  }

  function readQty(panel, selector) {
    if (!selector) return 0;
    var el;
    try { el = panel.querySelector(selector); } catch (e) { el = null; }
    if (!el) return 0;
    var n = parseFloat((el.textContent || '').replace(/[^0-9.\-]/g, ''));
    if (isNaN(n) || n < 0) return 0;
    return Math.ceil(n);
  }

  function initPanel(panel) {
    if (panel.__ftbuyInit) return;
    var groups = Array.prototype.slice.call(panel.querySelectorAll('[data-ftbuy-tiles]'));
    if (!groups.length) return;
    panel.__ftbuyInit = true;

    var addBtn = panel.querySelector('[data-ftbuy-add]');
    var totalEl = panel.querySelector('[data-ftbuy-total]');
    var errEl = panel.querySelector('[data-ftbuy-error]');

    function showError(msg) {
      if (errEl) { errEl.textContent = msg || ''; errEl.hidden = !msg; }
      else if (msg) { alert(msg); }
    }

    var state = groups.map(function (g) {
      var key = g.getAttribute('data-ftbuy-tiles');
      return {
        key: key,
        mode: g.getAttribute('data-ftbuy-mode') || 'choice',
        qtySelector: g.getAttribute('data-ftbuy-qty-selector'),
        groupEl: g,
        tiles: Array.prototype.slice.call(g.querySelectorAll('[data-ftbuy-tile]')),
        line: panel.querySelector('[data-ftbuy-line="' + key + '"]')
      };
    });

    function setActive(g, tile) {
      g.tiles.forEach(function (t) {
        var on = t === tile;
        t.classList.toggle('ft-calc-buy__tile--active', on);
        if (t.getAttribute('role') === 'radio') {
          t.setAttribute('aria-checked', on ? 'true' : 'false');
          t.tabIndex = on ? 0 : -1;
        }
      });
    }

    function selectedTile(g) {
      if (!g.tiles.length) return null;
      if (g.mode === 'auto') {
        var qty = readQty(panel, g.qtySelector);
        var chosen = null;
        g.tiles.forEach(function (t) {
          var min = parseFloat(t.getAttribute('data-min')); if (isNaN(min)) min = 0;
          var max = parseFloat(t.getAttribute('data-max')); if (isNaN(max)) max = Infinity;
          if (qty >= min && qty <= max) chosen = t;
        });
        if (!chosen) chosen = qty > 0 ? g.tiles[g.tiles.length - 1] : g.tiles[0];
        g.tiles.forEach(function (t) { t.classList.toggle('ft-calc-buy__tile--active', t === chosen); });
        return chosen;
      }
      var active = g.groupEl.querySelector('.ft-calc-buy__tile--active');
      return active || g.tiles[0];
    }

    function recompute() {
      var grand = 0;
      state.forEach(function (g) {
        var qty = readQty(panel, g.qtySelector);
        var tile = selectedTile(g);
        var price = tile ? (parseFloat(tile.getAttribute('data-price')) || 0) : 0;
        var lineTotal = price * qty;
        grand += lineTotal;
        if (g.line) {
          var q = g.line.querySelector('[data-ftbuy-line-qty]'); if (q) q.textContent = qty;
          var pu = g.line.querySelector('[data-ftbuy-line-unit]'); if (pu) pu.textContent = money(price);
          var lt = g.line.querySelector('[data-ftbuy-line-total]'); if (lt) lt.textContent = money(lineTotal);
          var nm = g.line.querySelector('[data-ftbuy-line-name]');
          if (nm && tile && tile.getAttribute('data-name')) nm.textContent = tile.getAttribute('data-name');
        }
      });
      if (totalEl) totalEl.textContent = money(grand);
    }

    // Choice-mode selection + keyboard (ARIA radiogroup pattern)
    state.forEach(function (g) {
      if (g.mode === 'auto') return;
      var active = g.groupEl.querySelector('.ft-calc-buy__tile--active');
      setActive(g, active || g.tiles[0]);
      g.tiles.forEach(function (tile, idx) {
        tile.addEventListener('click', function (e) {
          e.preventDefault();
          setActive(g, tile);
          showError('');
          recompute();
        });
        tile.addEventListener('keydown', function (e) {
          var next = -1;
          if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (idx + 1) % g.tiles.length;
          else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = (idx - 1 + g.tiles.length) % g.tiles.length;
          else if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); setActive(g, tile); showError(''); recompute(); return; }
          if (next > -1) { e.preventDefault(); setActive(g, g.tiles[next]); g.tiles[next].focus(); recompute(); }
        });
      });
    });

    // React to the calculator recomputing its count
    state.forEach(function (g) {
      if (!g.qtySelector) return;
      var el;
      try { el = panel.querySelector(g.qtySelector); } catch (e) { el = null; }
      if (el && window.MutationObserver) {
        new MutationObserver(recompute).observe(el, { childList: true, characterData: true, subtree: true });
      }
    });
    panel.addEventListener('input', function () { setTimeout(recompute, 0); });
    panel.addEventListener('change', function () { setTimeout(recompute, 0); });

    if (addBtn) {
      addBtn.addEventListener('click', function () {
        var items = [];
        var missing = false;
        var anyQty = false;
        state.forEach(function (g) {
          var qty = readQty(panel, g.qtySelector);
          if (qty < 1) return;
          anyQty = true;
          var tile = selectedTile(g);
          if (!tile) return;
          var variantId = tile.getAttribute('data-variant-id');
          var handle = tile.getAttribute('data-product-handle');
          if (variantId) { items.push({ id: parseInt(variantId, 10), quantity: qty }); }
          else if (handle) { items.push({ __handle: handle, quantity: qty }); }
          else { missing = true; }
        });
        if (!anyQty) { showError('Enter your measurements above to get a quantity first.'); return; }
        if (missing) { showError('Sorry — this option isn’t available to add right now.'); return; }
        if (!items.length) { return; }
        showError('');

        addBtn.disabled = true;
        panel.setAttribute('aria-busy', 'true');

        Promise.all(items.map(function (it) {
          if (it.id) return Promise.resolve({ id: it.id, quantity: it.quantity });
          return fetch('/products/' + it.__handle + '.js')
            .then(function (r) { return r.json(); })
            .then(function (p) { return { id: p.variants[0].id, quantity: it.quantity }; });
        })).then(function (resolved) {
          return fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: resolved })
          });
        }).then(function (res) {
          if (!res.ok) throw new Error('cart');
          window.location.href = '/cart';
        }).catch(function () {
          addBtn.disabled = false;
          panel.removeAttribute('aria-busy');
          showError('Something went wrong adding to cart. Please try again.');
        });
      });
    }

    recompute();
  }

  function initAll() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-ftbuy-panel]'), initPanel);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', initAll);
})();
